import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { calculateAll, type CipherResult } from '@gematria/shared'

const API_URL = import.meta.env.PROD ? '' : 'http://localhost:3001'

// Cipher colors
const cipherColors: Record<string, { name: string; value: string; bg: string }> = {
  'Alphanumeric Qabbala': { name: 'text-stone-300', value: 'text-stone-100', bg: 'bg-stone-900' },
  'AQ Squares': { name: 'text-[#B89B6B]', value: 'text-[#D4B896]', bg: 'bg-[#2A2015]' },
  'AQ Primes': { name: 'text-[#D4B896]', value: 'text-[#EBD4B0]', bg: 'bg-[#352A1F]' },
  'AQ Trigonal': { name: 'text-[#EBD4B0]', value: 'text-[#F5E6D0]', bg: 'bg-[#403526]' },
  'Ordinal': { name: 'text-stone-200', value: 'text-stone-100', bg: 'bg-stone-700' },
  'Numeric QWERTY': { name: 'text-green-400', value: 'text-green-200', bg: 'bg-green-950' },
  'NQ Squares': { name: 'text-[#5A9B5A]', value: 'text-[#7ABD7A]', bg: 'bg-[#0F2510]' },
  'NQ Primes': { name: 'text-[#6ABD6A]', value: 'text-[#8BDF8B]', bg: 'bg-[#1A351A]' },
  'NQ Trigonal': { name: 'text-[#8BDF8B]', value: 'text-[#ABFFAB]', bg: 'bg-[#2A452A]' },
  'QWERTY': { name: 'text-lime-400', value: 'text-lime-200', bg: 'bg-lime-950' },
  'Standard': { name: 'text-[#E8A830]', value: 'text-[#F8C040]', bg: 'bg-[#3D2815]' },
  'Satanic': { name: 'text-red-500', value: 'text-red-300', bg: 'bg-red-950' },
  'Synx': { name: 'text-cyan-400', value: 'text-cyan-200', bg: 'bg-cyan-950' },
  'Reduction': { name: 'text-[#F0B840]', value: 'text-[#F8D060]', bg: 'bg-[#4A3220]' },
  'Vaxen': { name: 'text-violet-400', value: 'text-violet-200', bg: 'bg-violet-950' },
  'Unun': { name: 'text-indigo-400', value: 'text-indigo-200', bg: 'bg-indigo-950' },
  'Based Atlanteanism': { name: 'text-amber-500', value: 'text-amber-300', bg: 'bg-amber-950' },
}

// Cipher order matching private profile
const cipherOrder = [
  'Alphanumeric Qabbala', 'Ordinal', 'AQ Primes', 'AQ Squares', 'AQ Trigonal',
  'Numeric QWERTY', 'QWERTY', 'NQ Primes', 'NQ Squares', 'NQ Trigonal',
  'Synx', 'Unun', 'Vaxen', 'Based Atlanteanism', 'Satanic', 'Standard', 'Reduction'
]

const cipherDisplayNames: Record<string, string> = {
  'Alphanumeric Qabbala': 'AQ',
  'Numeric QWERTY': 'NQ',
}

const tripleNumbers = [111, 222, 333, 444, 555, 666, 777, 888, 999, 1111, 2222, 3333, 4444, 5555, 6666, 7777, 8888, 9999]

interface PublicProfile {
  username: string
  bio: string
  pinned_phrase: { phrase: string; results: CipherResult[] } | null
  discovery_count: number
}

interface PublicPhrase {
  id: string
  phrase_hash: string
  phrase: string
  results: CipherResult[]
  category: string
  tags: string[]
  discoveredBy?: string | null
  like_count: number
  userLiked: boolean
}

export function PublicProfilePage() {
  const navigate = useNavigate()
  const { username } = useParams<{ username: string }>()
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [phrases, setPhrases] = useState<PublicPhrase[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Search states (matching private profile)
  const [searchFilter, setSearchFilter] = useState('')
  const [searchByValue, setSearchByValue] = useState(false)
  const [visibleCiphers, setVisibleCiphers] = useState<Set<string>>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('publicVisibleCiphers')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) {
          return new Set(parsed)
        }
      } catch {
        return new Set()
      }
    }
    return new Set()
  })
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [discovererError, setDiscovererError] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState<string | null>(null)
  const [isCopying, setIsCopying] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<{username: string} | null>(null)
  const [cipherSaveMessage, setCipherSaveMessage] = useState(false)
  const [cipherSaveFading, setCipherSaveFading] = useState(false)
  const [cipherLoadMessage, setCipherLoadMessage] = useState(false)
  const [cipherLoadFading, setCipherLoadFading] = useState(false)

  // Selection state for bulk save
  const [selectedPhrases, setSelectedPhrases] = useState<Set<string>>(new Set())
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectionBox, setSelectionBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null)
  const phrasesGridRef = useRef<HTMLDivElement>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)

  // Auth guard - redirect unsigned users
  if (!localStorage.getItem('session')) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <div className="text-center">
          <p className="text-green-400 text-lg mb-4">Sign in to view profiles</p>
          <Link to="/" className="text-green-400 hover:text-green-300 underline">Go to Calculator</Link>
        </div>
      </div>
    )
  }

  // Highlight settings from localStorage (sync with Calculator)
  const [highlightedPhrases] = useState<string[]>(() => {
    try { const s = localStorage.getItem('highlightedPhrases'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [userHighlightedValues, setUserHighlightedValues] = useState<number[]>(() => {
    try { const s = localStorage.getItem('userHighlightedValues'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [disabledDefaultValues, setDisabledDefaultValues] = useState<number[]>(() => {
    try { const s = localStorage.getItem('disabledDefaultValues'); return s ? JSON.parse(s) : [] } catch { return [] }
  })

  const shouldHighlightValue = (value: number): boolean => {
    if (userHighlightedValues.includes(value)) return true
    if (tripleNumbers.includes(value) && !disabledDefaultValues.includes(value)) return true
    return false
  }

  // Listen for highlight changes from other tabs
  useEffect(() => {
    const handler = () => {
      try {
        const s = localStorage.getItem('userHighlightedValues')
        if (s) setUserHighlightedValues(JSON.parse(s))
        const d = localStorage.getItem('disabledDefaultValues')
        if (d) setDisabledDefaultValues(JSON.parse(d))
      } catch {}
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  // Load current user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        setCurrentUser(user)
      } catch (e) {
        console.error('Failed to parse user data:', e)
      }
    }
  }, [])

  // Load saved cipher selections from server
  useEffect(() => {
    loadSavedCipherSelection()
  }, [])

  const loadSavedCipherSelection = async () => {
    const session = localStorage.getItem('session')
    if (!session) return
    
    try {
      const response = await fetch(`${API_URL}/api/settings/visibleCiphers`, {
        headers: { 'Authorization': `Bearer ${session}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.setting_value) {
          const savedCiphers = JSON.parse(data.setting_value)
          if (Array.isArray(savedCiphers)) {
            setVisibleCiphers(new Set(savedCiphers))
            // Show "Loaded" message
            setCipherLoadMessage(true)
            setCipherLoadFading(false)
            setTimeout(() => {
              setCipherLoadFading(true)
            }, 300)
            setTimeout(() => {
              setCipherLoadMessage(false)
              setCipherLoadFading(false)
            }, 500)
          }
        }
      }
    } catch (error) {
      console.error('Failed to load saved cipher selection:', error)
    }
  }

  // Save visibleCiphers to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('publicVisibleCiphers', JSON.stringify([...visibleCiphers]))
  }, [visibleCiphers])

  // Check if discoverer has a public profile
  const checkDiscovererProfile = async (discovererUsername: string) => {
    if (discovererUsername === username) {
      // Already viewing this profile
      return
    }
    
    try {
      const response = await fetch(`${API_URL}/api/users/${discovererUsername}`)
      if (response.ok) {
        // Profile exists and is public - navigate to it
        navigate(`/u/${discovererUsername}`)
      } else if (response.status === 403) {
        setDiscovererError(`${discovererUsername} has not made their profile public`)
        setTimeout(() => setDiscovererError(null), 3000)
      } else if (response.status === 404) {
        setDiscovererError(`User ${discovererUsername} not found`)
        setTimeout(() => setDiscovererError(null), 3000)
      } else {
        setDiscovererError('Unable to view this profile')
        setTimeout(() => setDiscovererError(null), 3000)
      }
    } catch (error) {
      setDiscovererError('Unable to view this profile')
      setTimeout(() => setDiscovererError(null), 3000)
    }
  }

  // Copy phrase to user's profile
  const copyPhrase = async (phraseId: string, phraseText: string) => {
    const session = localStorage.getItem('session')
    
    if (!session) {
      setDiscovererError('Please sign in to copy phrases')
      setTimeout(() => setDiscovererError(null), 3000)
      return
    }
    
    setIsCopying(phraseId)
    const csrfToken = localStorage.getItem('csrfToken')
    
    try {
      const response = await fetch(`${API_URL}/api/phrases/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          source_phrase_id: phraseId,
          target_category: 'uncategorized',
          copy_tags: true
        })
      })
      
      if (response.ok) {
        setCopySuccess(`Copied "${phraseText.substring(0, 20)}${phraseText.length > 20 ? '...' : ''}" to your profile`)
        setTimeout(() => setCopySuccess(null), 3000)
      } else if (response.status === 401) {
        setDiscovererError('Please sign in to copy phrases')
        setTimeout(() => setDiscovererError(null), 3000)
      } else {
        const errorData = await response.json().catch(() => ({}))
        setDiscovererError(errorData.error || 'Failed to copy phrase')
        setTimeout(() => setDiscovererError(null), 3000)
      }
    } catch (error) {
      setDiscovererError('Network error. Please try again.')
      setTimeout(() => setDiscovererError(null), 3000)
    } finally {
      setIsCopying(null)
    }
  }

  // Bulk copy selected phrases
  const bulkCopyPhrases = async () => {
    console.log('bulkCopyPhrases called', { selectedPhrases: Array.from(selectedPhrases) })
    if (selectedPhrases.size === 0) return
    
    const session = localStorage.getItem('session')
    if (!session) {
      console.log('No session found')
      setDiscovererError('Please sign in to copy phrases')
      setTimeout(() => setDiscovererError(null), 3000)
      return
    }

    const phraseIds = Array.from(selectedPhrases)
    let copiedCount = 0
    const csrfToken = localStorage.getItem('csrfToken')

    for (const phraseId of phraseIds) {
      console.log('Copying phrase:', phraseId)
      try {
        const response = await fetch(`${API_URL}/api/phrases/copy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          },
          body: JSON.stringify({
            source_phrase_id: phraseId,
            target_category: 'uncategorized',
            copy_tags: true
          })
        })
        
        console.log('Copy response status:', response.status)
        if (response.ok) {
          copiedCount++
        } else {
          const errorData = await response.json().catch(() => ({}))
          console.error('Copy failed:', response.status, errorData)
        }
      } catch (error) {
        console.error('Failed to copy phrase:', phraseId, error)
      }
    }

    console.log('Copied count:', copiedCount)
    if (copiedCount > 0) {
      setCopySuccess(`Copied ${copiedCount} phrase${copiedCount > 1 ? 's' : ''} to your profile`)
      setTimeout(() => setCopySuccess(null), 3000)
    } else {
      setDiscovererError('Failed to copy phrases')
      setTimeout(() => setDiscovererError(null), 3000)
    }
    
    setSelectedPhrases(new Set())
  }

  const toggleLike = async (phraseHash: string) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (!session || !csrfToken) return
    try {
      const response = await fetch(`${API_URL}/api/discoveries/${phraseHash}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken,
        },
      })
      if (!response.ok) throw new Error('Failed to toggle like')
      const data = await response.json()
      setPhrases(prev => prev.map(p =>
        p.phrase_hash === phraseHash
          ? { ...p, like_count: data.like_count, userLiked: data.liked }
          : p
      ))
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }

  // Selection box handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'A' || 
        target.closest('button') || target.closest('input') || target.closest('a') ||
        target.closest('[data-phrase-id]')) {
      return
    }
    
    if (e.metaKey || e.ctrlKey) return
    e.preventDefault()
    
    const gridRect = phrasesGridRef.current?.getBoundingClientRect()
    if (!gridRect) return

    const x = e.clientX - gridRect.left
    const y = e.clientY - gridRect.top

    setIsSelecting(true)
    setSelectionBox({ startX: x, startY: y, currentX: x, currentY: y })
    if (!e.shiftKey) {
      setSelectedPhrases(new Set())
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !selectionBox || !phrasesGridRef.current) return

    e.preventDefault()
    const rect = phrasesGridRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setSelectionBox(prev => prev ? { ...prev, currentX: x, currentY: y } : null)

    const phraseCards = phrasesGridRef.current.querySelectorAll('[data-phrase-id]')
    const newSelection = new Set<string>(e.shiftKey ? selectedPhrases : [])

    phraseCards.forEach((card) => {
      const cardRect = card.getBoundingClientRect()
      const boxLeft = Math.min(selectionBox.startX, selectionBox.currentX) + rect.left
      const boxRight = Math.max(selectionBox.startX, selectionBox.currentX) + rect.left
      const boxTop = Math.min(selectionBox.startY, selectionBox.currentY) + rect.top
      const boxBottom = Math.max(selectionBox.startY, selectionBox.currentY) + rect.top

      const intersects = !(
        cardRect.right < boxLeft ||
        cardRect.left > boxRight ||
        cardRect.bottom < boxTop ||
        cardRect.top > boxBottom
      )

      if (intersects) {
        const phraseId = (card as HTMLElement).dataset.phraseId
        if (phraseId) newSelection.add(phraseId)
      }
    })

    setSelectedPhrases(newSelection)
  }

  const handleMouseUp = () => {
    setIsSelecting(false)
    setSelectionBox(null)
  }

  useEffect(() => {
    if (username) {
      fetchProfile()
      fetchPhrases()
    }
  }, [username])

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${username}`)
      if (response.ok) {
        const data = await response.json()
        setProfile(data)
      } else if (response.status === 404) {
        setError('User not found')
      } else if (response.status === 403) {
        setError('Profile is private')
      }
    } catch (error) {
      setError('Failed to load profile')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPhrases = async () => {
    try {
      const session = localStorage.getItem('session')
      const response = await fetch(`${API_URL}/api/users/${username}/phrases`, {
        headers: session ? { 'Authorization': `Bearer ${session}` } : {}
      })
      if (response.ok) {
        const data = await response.json()
        setPhrases(data)
      }
    } catch (error) {
      console.error('Failed to fetch phrases:', error)
    }
  }

  // Get all unique tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    phrases.forEach(p => p.tags?.forEach(tag => tagSet.add(tag)))
    return Array.from(tagSet).sort()
  }, [phrases])

  // Group phrases by category
  const phrasesByCategory = useMemo(() => {
    const grouped: Record<string, PublicPhrase[]> = {}
    phrases.forEach(phrase => {
      const category = phrase.category || 'uncategorized'
      if (!grouped[category]) grouped[category] = []
      grouped[category].push(phrase)
    })
    return grouped
  }, [phrases])

  // Filter function (matching private profile logic)
  const filterPhrase = (phrase: PublicPhrase): boolean => {
    const searchTerms = searchFilter
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0)
    
    if (searchTerms.length === 0) return true
    
    if (searchByValue) {
      return searchTerms.some(term => {
        const searchNum = parseInt(term, 10)
        if (isNaN(searchNum)) return false
        // Recalculate to include numeric digit support
        const calculatedResults = calculateAll(phrase.phrase)
        return calculatedResults.some(result => result.value === searchNum)
      })
    }
    
    return searchTerms.some(term => {
      const termLower = term.toLowerCase()
      const matchesPhrase = phrase.phrase.toLowerCase().includes(termLower)
      const matchesCategory = (phrase.category || 'uncategorized').toLowerCase().includes(termLower)
      const matchesTags = phrase.tags?.some(t => t.toLowerCase().includes(termLower))
      return matchesPhrase || matchesCategory || matchesTags
    })
  }

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(category)) {
        newSet.delete(category)
      } else {
        newSet.add(category)
      }
      return newSet
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <p className="text-green-600">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/" className="text-green-400 hover:text-green-300 underline">
            Return to Calculator
          </Link>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <p className="text-green-600">Profile not found</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Special Elite', cursive" }}>
      {/* Header */}
      <header className="border-b border-green-900 bg-black/50 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-lg font-bold text-green-400 hover:text-green-300 transition-colors">
                ← Calculator
              </Link>
            </div>
            <div className="flex items-center gap-3">
              {currentUser && (
                <>
                  <Link 
                    to="/profile"
                    className="text-green-400 hover:text-green-300 transition-colors"
                    title="Go to your vault"
                  >
                    Vault
                  </Link>
                  <Link 
                    to="/feed"
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    Feed
                  </Link>
                  <Link 
                    to="/settings"
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    Settings
                  </Link>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('session')
                      localStorage.removeItem('csrfToken')
                      localStorage.removeItem('user')
                      navigate('/')
                    }}
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Discoverer Profile Error Toast */}
      {discovererError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-red-900/90 border border-red-500 text-red-200 px-6 py-3 rounded-lg shadow-lg">
          {discovererError}
        </div>
      )}

      {/* Copy Success Toast */}
      {copySuccess && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-green-900/90 border border-green-500 text-green-200 px-6 py-3 rounded-lg shadow-lg">
          {copySuccess}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Username */}
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold !text-green-400" style={{ fontFamily: 'UnifrakturMaguntia, Old English Text MT, Parchment, serif' }}>{profile.username}</h1>
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="mb-6 p-4 bg-green-950/20 border border-green-800 rounded-lg text-center">
            <div className="text-xs text-green-500 uppercase tracking-wider mb-2 font-semibold">Bio</div>
            <p className="text-green-300">{profile.bio}</p>
            <div className="mt-3 pt-3 border-t border-green-800/50">
              <span className="text-amber-400 text-sm flex items-center justify-center gap-2" title={`${profile.discovery_count} phrases discovered`}>
                <span className="text-amber-300">{profile.discovery_count}</span>
                <span className="text-amber-300/70 text-xs">Discovered phrases</span>
              </span>
            </div>
          </div>
        )}

        {/* Pinned Phrase - centered under bio */}
        {profile.pinned_phrase && (
          <div className="mb-8 text-center">
            <div className="inline-block">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-xl font-bold text-amber-400">{profile.pinned_phrase.phrase}</span>
              </div>
              {profile.pinned_phrase.phrase && (
                <div className="flex flex-wrap justify-center gap-3 text-sm">
                  {(() => {
                    // Recalculate to include numeric digit support
                    const calculatedResults = calculateAll(profile.pinned_phrase!.phrase)
                    return calculatedResults.map((result, idx) => {
                      const colors = cipherColors[result.name]
                      const displayName = cipherDisplayNames[result.name] || result.name
                      const isTriple = tripleNumbers.includes(result.value)
                      return (
                        <span key={idx} className="bg-green-900/30 px-2 py-1 rounded">
                          <span className={colors.name}>{displayName}:</span>{' '}
                          <span 
                            className={`font-mono font-bold ${isTriple ? 'text-amber-400' : colors.value}`}
                            style={isTriple ? { textShadow: '0 0 8px rgba(251, 191, 36, 0.8), 0 0 16px rgba(251, 191, 36, 0.4)' } : undefined}
                          >{result.value}</span>
                        </span>
                      )
                    })
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search & Cipher Toggle (matching private profile) */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-semibold text-green-400">Search</h3>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchByValue}
                  onChange={(e) => setSearchByValue(e.target.checked)}
                  className="w-4 h-4 accent-green-500"
                />
                <span className="text-green-400">Lookup by key</span>
              </label>
            </div>
          </div>
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              placeholder={searchByValue ? "Enter cipher value (e.g., 73, 666)" : "Search by phrase, folder, or tag..."}
              value={searchFilter}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^a-zA-Z0-9,.\s]/g, '')
                setSearchFilter(sanitized)
              }}
              className="flex-1 px-4 py-3 bg-black border border-green-700 rounded-lg focus:border-green-400 focus:outline-none text-green-400"
            />
            {searchFilter && (
              <button
                onClick={() => setSearchFilter('')}
                className="px-4 py-3 bg-green-900/50 border border-green-600 text-green-400 rounded-lg hover:bg-green-900/70"
              >
                Clear ×
              </button>
            )}
          </div>
          
          {/* Tag Cloud */}
          {allTags.length > 0 && !searchByValue && (
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-green-600 text-sm py-1">Filter by tag:</span>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSearchFilter(prev => prev ? `${prev}, ${tag}` : tag)}
                  className="px-3 py-1 rounded-full text-sm bg-green-900/50 text-green-400 hover:bg-green-800/50 transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
          
          {/* Cipher Toggles */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-green-400">Show Values</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setVisibleCiphers(new Set(cipherOrder))}
                  className="text-xs text-green-500 hover:text-green-400"
                >
                  All
                </button>
                <button
                  onClick={() => setVisibleCiphers(new Set())}
                  className="text-xs text-green-500 hover:text-green-400"
                >
                  None
                </button>
                <button
                  onClick={async () => {
                    const session = localStorage.getItem('session')
                    if (!session) {
                      alert('Please sign in to save cipher selections')
                      return
                    }
                    
                    const ciphersToSave = Array.from(visibleCiphers)
                    const csrfToken = localStorage.getItem('csrfToken')
                    try {
                      const response = await fetch(`${API_URL}/api/settings/visibleCiphers`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${session}`,
                          'X-CSRF-Token': csrfToken || ''
                        },
                        body: JSON.stringify({ value: JSON.stringify(ciphersToSave) })
                      })
                      
                      if (response.ok) {
                        setCipherSaveMessage(true)
                        setCipherSaveFading(false)
                        // Start fade after 0.3 seconds
                        setTimeout(() => {
                          setCipherSaveFading(true)
                        }, 300)
                        // Remove completely after fade completes
                        setTimeout(() => {
                          setCipherSaveMessage(false)
                          setCipherSaveFading(false)
                        }, 500)
                      } else {
                        alert('Failed to save cipher selections')
                      }
                    } catch (error) {
                      alert('Network error. Please try again.')
                    }
                  }}
                  className="text-xs text-amber-500 hover:text-amber-400"
                >
                  Save
                </button>
                <button
                  onClick={loadSavedCipherSelection}
                  className="text-xs text-amber-500 hover:text-amber-400"
                >
                  Load
                </button>
                {cipherSaveMessage && (
                  <span 
                    className={`text-xs text-amber-400 ml-2 transition-opacity duration-500 ${cipherSaveFading ? 'opacity-0' : 'opacity-100'}`}
                  >
                    Saved
                  </span>
                )}
                {cipherLoadMessage && (
                  <span 
                    className={`text-xs text-amber-400 ml-2 transition-opacity duration-500 ${cipherLoadFading ? 'opacity-0' : 'opacity-100'}`}
                  >
                    Loaded
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const availableCiphers = new Set<string>()
                phrases.forEach(p => p.results?.forEach(r => availableCiphers.add(r.name)))
                return cipherOrder
                  .filter(cipherName => availableCiphers.has(cipherName))
                  .map(cipherName => {
                    const colors = cipherColors[cipherName]
                    return (
                      <label key={cipherName} className={`flex items-center gap-1 text-xs cursor-pointer hover:bg-green-900/30 px-2 py-1 rounded border border-green-800 ${colors.name}`}>
                        <input
                          type="checkbox"
                          checked={visibleCiphers.has(cipherName)}
                          onChange={() => {
                            setVisibleCiphers(prev => {
                              const newSet = new Set(prev)
                              if (newSet.has(cipherName)) {
                                newSet.delete(cipherName)
                              } else {
                                newSet.add(cipherName)
                              }
                              return newSet
                            })
                          }}
                          className="w-3 h-3 accent-green-500"
                        />
                        <span className="font-bold">{cipherDisplayNames[cipherName] || cipherName}</span>
                      </label>
                    )
                  })
              })()}
            </div>
          </div>
        </div>

        {/* Selection Instructions */}
        <div className="mb-4 text-xs text-green-500 flex items-center gap-4">
          <span>
            <strong className="text-green-400">Tip:</strong> Click and drag to select multiple phrases
          </span>
          {selectedPhrases.size > 0 && (
            <span className="px-2 py-1 bg-green-900/50 rounded text-green-400">
              {selectedPhrases.size} selected
            </span>
          )}
        </div>

        {/* Folders */}
        <div 
          ref={mainContainerRef}
          className={`grid grid-cols-1 lg:grid-cols-4 gap-6 ${isSelecting ? 'select-none' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Sidebar - Folders */}
          <div className="lg:col-span-1">
            <div className="bg-green-950/20 border border-green-800 rounded-lg p-4 sticky top-24">
              <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                <span>📁</span> Folders
              </h3>
              
              <div className="space-y-1">
                {Object.keys(phrasesByCategory).sort().map(category => {
                  const phrasesInCategory = phrasesByCategory[category]
                  const filteredCount = phrasesInCategory.filter(filterPhrase).length
                  const isExpanded = expandedCategories.has(category)
                  
                  return (
                    <div key={category} className="border border-green-800 rounded-lg overflow-hidden">
                      <div
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-green-900/30 hover:bg-green-900/50 transition-colors text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-green-400">{isExpanded ? '📂' : '📁'}</span>
                          <span className="text-green-300 text-sm font-medium">{category}</span>
                        </div>
                        <span className="text-green-600 text-xs">{filteredCount}</span>
                      </div>
                      
                      {isExpanded && (
                        <div className="bg-black/50">
                          {filteredCount === 0 ? (
                            <p className="text-green-700 text-xs px-3 py-2 italic">
                              {searchFilter ? 'No matching phrases' : 'Empty folder'}
                            </p>
                          ) : (
                            phrasesInCategory
                              .filter(filterPhrase)
                              .map(phrase => (
                                <div
                                  key={phrase.id}
                                  className="px-3 py-2 border-t border-green-900/50"
                                >
                                  <p className="text-green-400 text-sm truncate">{phrase.phrase}</p>
                                </div>
                              ))
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Main Content - All Public Phrases */}
          <div className="lg:col-span-3">
            <h3 className="text-lg font-semibold text-green-400 mb-4">
              {phrases.filter(filterPhrase).length} Matching Phrases
            </h3>

            {phrases.filter(filterPhrase).length === 0 ? (
              <div className="text-center py-12 border border-green-800 rounded-lg">
                <p className="text-green-600">
                  {searchFilter ? 'No phrases match your search' : 'No public phrases available'}
                </p>
              </div>
            ) : (
              <div 
                ref={phrasesGridRef}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 relative"
              >
                {/* Selection Box Overlay */}
                {selectionBox && (
                  <div
                    className="absolute border-2 border-green-400 bg-green-400/20 pointer-events-none z-10"
                    style={{
                      left: Math.min(selectionBox.startX, selectionBox.currentX),
                      top: Math.min(selectionBox.startY, selectionBox.currentY),
                      width: Math.abs(selectionBox.currentX - selectionBox.startX),
                      height: Math.abs(selectionBox.currentY - selectionBox.startY),
                    }}
                  />
                )}
                {phrases
                  .filter(filterPhrase)
                  .map((phrase) => {
                    const isSelected = selectedPhrases.has(phrase.id)
                    return (
                    <div 
                      key={phrase.id} 
                      data-phrase-id={phrase.id}
                      className={`border rounded-lg p-4 transition-colors bg-green-950/10 flex flex-col cursor-pointer ${
                        isSelected 
                          ? 'border-green-400 ring-2 ring-green-400 bg-green-400/20' 
                          : 'border-green-800 hover:border-green-600'
                      }`}
                      onClick={(_e) => {
                        if (!isSelecting) {
                          const newSelection = new Set(selectedPhrases)
                          if (newSelection.has(phrase.id)) {
                            newSelection.delete(phrase.id)
                          } else {
                            newSelection.add(phrase.id)
                          }
                          setSelectedPhrases(newSelection)
                        }
                      }}
                    >
                      <p className={`text-lg mb-2 text-center ${highlightedPhrases.some(hp => hp.toLowerCase() === phrase.phrase.toLowerCase()) ? 'text-amber-400 font-bold' : 'text-green-300 font-medium'}`}>{phrase.phrase}</p>
                      
                      {/* Gematria Values */}
                      {visibleCiphers.size > 0 && (
                        <div className="flex flex-wrap justify-center gap-3 mb-3 text-xs">
                          {(() => {
                            // Recalculate to include numeric digit support
                            const calculatedResults = calculateAll(phrase.phrase)
                            return calculatedResults
                              .filter(result => visibleCiphers.has(result.name))
                              .map((result, idx) => {
                                const colors = cipherColors[result.name]
                                const displayName = cipherDisplayNames[result.name] || result.name
                                const isHighlighted = shouldHighlightValue(result.value)
                                  return (
                                    <span key={idx}>
                                      <span className={colors.name}>{displayName}:</span>{' '}
                                      <span 
                                        className={`font-mono font-semibold ${isHighlighted ? 'text-amber-400' : colors.value}`}
                                        style={isHighlighted ? { textShadow: '0 0 8px rgba(251, 191, 36, 0.8), 0 0 16px rgba(251, 191, 36, 0.4)' } : undefined}
                                      >{result.value}</span>
                                  </span>
                                )
                              })
                          })()}
                        </div>
                      )}
                      
                      {/* Category & Tags */}
                      <div className="flex flex-wrap gap-2 text-xs items-center mb-2">
                        {phrase.category && phrase.category !== 'uncategorized' && (
                          <span className="px-2 py-1 bg-green-900/50 text-green-400 rounded">
                            📁 {phrase.category}
                          </span>
                        )}
                        {phrase.tags && phrase.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-green-800/50 text-green-300 rounded"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      
                      {/* Bottom Row - Discovered by (left) and Save (right) */}
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <div className="flex items-center gap-3">
                          {phrase.discoveredBy && (
                            <span 
                              className="text-green-600 text-xs"
                              title={`First discovered by ${phrase.discoveredBy}`}
                            >
                              Discovered by: <button 
                                onClick={(e) => {
                                  e.stopPropagation()
                                  checkDiscovererProfile(phrase.discoveredBy!)
                                }}
                                className="text-green-500 hover:text-green-400 cursor-pointer"
                                style={{ background: 'none', border: 'none', padding: 0 }}
                              >{phrase.discoveredBy}</button>
                            </span>
                          )}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleLike(phrase.phrase_hash)
                              }}
                              className={`text-sm transition-colors ${phrase.userLiked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}`}
                            >
                              {phrase.userLiked ? '❤' : '♡'}
                            </button>
                            <span className="text-xs text-gray-400">{phrase.like_count}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (selectedPhrases.size > 0) {
                              bulkCopyPhrases()
                            } else {
                              copyPhrase(phrase.id, phrase.phrase)
                            }
                          }}
                          disabled={isCopying === phrase.id}
                          className="px-2 py-1 bg-green-700 text-green-200 rounded hover:bg-green-600 transition-colors text-xs flex items-center gap-1 disabled:opacity-50 border border-green-500/50"
                        >
                          {isCopying === phrase.id ? (
                            <span>Saving...</span>
                          ) : selectedPhrases.size > 0 ? (
                            <>Save all</>
                          ) : (
                            <>Save</>
                          )}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
